import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, CheckCircle2, ArrowRight, ArrowLeft, Send, Loader2 } from "lucide-react";
import { BUSINESS, SERVICES, CITIES } from "@/data/content";
import { SectionHeading, CTA, FinalCTA } from "@/components/Shared";
import { DarkHeroAccent } from "@/components/RoofAccents";
import { SEO } from "@/components/SEO";
import { submitContact, submitEstimate } from "@/lib/submit";

export function ContactPage() {
  const [form, setForm] = React.useState({ name: "", email: "", phone: "", city: "", service: "", message: "" });
  const [status, setStatus] = React.useState({ state: "idle", msg: "" });
  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    try {
      await submitContact(form);
      setStatus({ state: "success", msg: "Thanks - we'll be in touch within 24 hours." });
      setForm({ name: "", email: "", phone: "", city: "", service: "", message: "" });
    } catch (err) {
      setStatus({ state: "error", msg: "Something went wrong. Please try again or call us." });
    }
  };

  return (
    <div>
      <SEO
        title="Contact Goral Construction - Roofers in Waterford, MI"
        description="Call (248) 804-6933 or email goralconstruction.llc@yahoo.com. Free roofing estimates across Oakland County. Mon-Sat 7AM-7PM. 24-hr emergency."
        path="/contact"
      />
      <section className="bg-bg-dark text-white py-20 relative overflow-hidden group">
        <DarkHeroAccent tone="house" />
        <div className="container-x relative">
          <div className="overline text-brand">Contact</div>
          <h1 className="font-heading font-extrabold text-5xl md:text-7xl tracking-tight mt-3 max-w-3xl leading-[1.02]">Let's talk about your project.</h1>
          <p className="text-white/80 text-lg max-w-2xl mt-5 leading-relaxed">Call, email, or fill out the form - whichever is easiest. We respond to every inquiry within 24 hours.</p>
        </div>
      </section>
      <section className="py-20">
        <div className="container-x grid lg:grid-cols-2 gap-14">
          <div>
            <SectionHeading overline="Direct contact" title="Reach us however works for you." />
            <div className="mt-10 space-y-6">
              <div className="flex gap-4 items-start"><div className="w-12 h-12 bg-brand/10 text-brand flex items-center justify-center rounded-sm flex-shrink-0"><Phone className="w-5 h-5" /></div><div><div className="font-heading text-lg font-bold">Phone</div><a href={`tel:${BUSINESS.phoneTel}`} className="text-ink-soft hover:text-brand">{BUSINESS.phone}</a><div className="text-xs text-ink-soft mt-1">Mon-Sat, 7AM-7PM · 24-hr emergency available</div></div></div>
              <div className="flex gap-4 items-start"><div className="w-12 h-12 bg-brand/10 text-brand flex items-center justify-center rounded-sm flex-shrink-0"><Mail className="w-5 h-5" /></div><div><div className="font-heading text-lg font-bold">Email</div><a href={`mailto:${BUSINESS.email}`} className="text-ink-soft hover:text-brand break-all">{BUSINESS.email}</a></div></div>
              <div className="flex gap-4 items-start"><div className="w-12 h-12 bg-brand/10 text-brand flex items-center justify-center rounded-sm flex-shrink-0"><MapPin className="w-5 h-5" /></div><div><div className="font-heading text-lg font-bold">Office</div><div className="text-ink-soft">{BUSINESS.address.full}</div></div></div>
              <div className="flex gap-4 items-start"><div className="w-12 h-12 bg-brand/10 text-brand flex items-center justify-center rounded-sm flex-shrink-0"><Clock className="w-5 h-5" /></div><div><div className="font-heading text-lg font-bold">Hours</div><div className="text-ink-soft">{BUSINESS.hours.weekdays}<br />{BUSINESS.hours.sunday}<br /><span className="text-brand font-semibold">{BUSINESS.hours.emergency}</span></div></div></div>
            </div>
            <div className="mt-10 aspect-[4/3] border border-ink-line overflow-hidden">
              <iframe title="Goral location" className="w-full h-full" src={`https://www.google.com/maps?q=${encodeURIComponent(BUSINESS.address.full)}&output=embed`} loading="lazy" />
            </div>
          </div>
          <div>
            <SectionHeading overline="Send a message" title="We'll get back within 24 hours." />
            {status.state === "success" ? (
              <div className="mt-8 p-8 bg-success/10 border-l-4 border-success"><CheckCircle2 className="w-8 h-8 text-success" /><div className="font-heading text-2xl font-bold mt-3">Message received</div><p className="text-ink-soft mt-2">{status.msg}</p></div>
            ) : (
              <form onSubmit={submit} className="mt-8 space-y-4" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-4">
                  <input required placeholder="Full name" value={form.name} onChange={e => update("name", e.target.value)} className="w-full px-4 py-3 border border-ink-line focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none rounded-sm" data-testid="contact-name" />
                  <input required type="email" placeholder="Email" value={form.email} onChange={e => update("email", e.target.value)} className="w-full px-4 py-3 border border-ink-line focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none rounded-sm" data-testid="contact-email" />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <input required placeholder="Phone" value={form.phone} onChange={e => update("phone", e.target.value)} className="w-full px-4 py-3 border border-ink-line focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none rounded-sm" data-testid="contact-phone" />
                  <select value={form.city} onChange={e => update("city", e.target.value)} className="w-full px-4 py-3 border border-ink-line focus:border-brand outline-none rounded-sm bg-white" data-testid="contact-city">
                    <option value="">City (optional)</option>
                    {CITIES.map(c => <option key={c.slug} value={c.name}>{c.name}, MI</option>)}
                    <option value="Other">Other</option>
                  </select>
                </div>
                <select value={form.service} onChange={e => update("service", e.target.value)} className="w-full px-4 py-3 border border-ink-line focus:border-brand outline-none rounded-sm bg-white" data-testid="contact-service">
                  <option value="">Service interest (optional)</option>
                  {SERVICES.map(s => <option key={s.slug} value={s.name}>{s.name}</option>)}
                </select>
                <textarea required rows={5} placeholder="Tell us about your project…" value={form.message} onChange={e => update("message", e.target.value)} className="w-full px-4 py-3 border border-ink-line focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none rounded-sm resize-none" data-testid="contact-message" />
                <button disabled={status.state === "loading"} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="contact-submit">
                  {status.state === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {status.state === "loading" ? "Sending…" : "Send message"}
                </button>
                {status.state === "error" && (
                  <div className="border-l-4 border-destructive bg-destructive/5 p-4" data-testid="contact-error">
                    <p className="font-heading font-bold text-destructive">Couldn't send your message.</p>
                    <p className="text-sm text-ink-soft mt-1 leading-relaxed">
                      Please try once more, or call{" "}
                      <a href={`tel:${BUSINESS.phoneTel}`} className="font-bold text-ink underline">{BUSINESS.phone}</a>
                      {" "}— we answer 7AM-7PM Mon-Sat.
                    </p>
                  </div>
                )}
                <p className="text-xs text-ink-soft">By submitting, you agree to our <Link to="/privacy-policy" className="underline">Privacy Policy</Link>.</p>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

// Estimate Wizard
// Map each service selection to the right "size" and "material" question set
// so a Siding lead is not asked about roof sq ft or roofing shingles,
// and a Gutters lead is asked about linear feet and gutter profiles (5" vs 6").
const SERVICE_PROFILE = {
  "New Roof": "roof",
  "Roof Repair": "roof",
  "Insurance Claim": "roof",
  "Not Sure": "roof",
  "Siding": "siding",
  "Gutters": "gutters",
};

const SIZE_STEP = {
  roof: {
    key: "roof_size",
    label: "Approx. Roof Size",
    options: ["Under 1,500 sq ft", "1,500-2,500 sq ft", "2,500-3,500 sq ft", "3,500+ sq ft", "Not sure"],
  },
  siding: {
    key: "roof_size",
    label: "Home Size & Stories",
    options: ["1-story, under 1,500 sq ft", "1-story, 1,500-2,500 sq ft", "2-story, under 2,500 sq ft", "2-story, 2,500+ sq ft", "Not sure"],
  },
  gutters: {
    key: "roof_size",
    label: "Approx. Linear Feet of Gutters",
    options: ["Under 100 ft", "100-150 ft", "150-200 ft", "200+ ft", "Not sure"],
  },
};

const MATERIAL_STEP = {
  roof: {
    key: "material",
    label: "Preferred Material",
    options: ["Asphalt Shingle (architectural)", "Asphalt Shingle (premium)", "Metal (standing seam)", "Flat (TPO/EPDM)", "Tile / Slate", "Recommend for me"],
  },
  siding: {
    key: "material",
    label: "Preferred Siding Material",
    options: ["Vinyl siding", "Insulated vinyl", "Fiber cement (James Hardie)", "Engineered wood (LP SmartSide)", "Aluminum / steel", "Recommend for me"],
  },
  gutters: {
    key: "material",
    label: "Gutter Profile & Material",
    options: ['5" K-style aluminum (standard)', '6" K-style aluminum (oversized)', "Half-round aluminum", "Copper", "Steel / galvanized", "Recommend for me"],
  },
};

const BASE_STEPS_BEFORE = [
  { key: "service_type", label: "Service Type", options: ["New Roof", "Roof Repair", "Siding", "Gutters", "Insurance Claim", "Not Sure"] },
  { key: "property_type", label: "Property Type", options: ["Single-family home", "Multi-family / Duplex", "Condo / Townhouse", "Commercial", "Other"] },
];

const BASE_STEP_AFTER = { key: "timeline", label: "Timeline", options: ["Emergency - ASAP", "Within 2 weeks", "1-3 months", "3-6 months", "Just researching"] };

function buildSteps(serviceType) {
  const profile = SERVICE_PROFILE[serviceType] || "roof";
  return [
    ...BASE_STEPS_BEFORE,
    SIZE_STEP[profile],
    MATERIAL_STEP[profile],
    BASE_STEP_AFTER,
  ];
}

export function EstimateWizardPage() {
  const [step, setStep] = React.useState(0);
  const [data, setData] = React.useState({ service_type: "", property_type: "", roof_size: "", material: "", timeline: "", name: "", email: "", phone: "", address: "", notes: "" });
  const [status, setStatus] = React.useState("idle");
  const [resultId, setResultId] = React.useState("");
  const STEPS = React.useMemo(() => buildSteps(data.service_type), [data.service_type]);
  const total = STEPS.length + 1;
  const progress = ((step + 1) / total) * 100;

  const choose = (key, value) => {
    setData(p => {
      const next = { ...p, [key]: value };
      // When service_type changes, reset downstream answers that are
      // service-specific so we don't carry stale "Asphalt Shingle" into a Siding quote.
      if (key === "service_type" && p.service_type !== value) {
        next.roof_size = "";
        next.material = "";
      }
      return next;
    });
    setTimeout(() => setStep(s => Math.min(s + 1, total - 1)), 150);
  };

  const submit = async () => {
    setStatus("loading");
    try {
      await submitEstimate(data);
      setResultId(Math.random().toString(36).slice(2, 10));
      setStatus("success");
    } catch { setStatus("error"); }
  };

  if (status === "success") {
    return (
      <div className="min-h-[80vh] flex items-center">
        <div className="container-x max-w-2xl text-center">
          <CheckCircle2 className="w-16 h-16 text-success mx-auto" />
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight mt-6">Request received.</h1>
          <p className="mt-4 text-ink-soft text-lg leading-relaxed">Thanks, {data.name.split(" ")[0]}. Maciej or a member of the crew will reach out within 24 hours to schedule your free on-site inspection.</p>
          <p className="mt-2 text-sm text-ink-soft">Reference #: <span className="font-mono">{resultId.slice(0, 8)}</span></p>
          <div className="mt-8 flex gap-3 justify-center"><Link to="/" className="btn-secondary">Back home</Link><Link to="/our-work" className="btn-primary">See our work <ArrowRight className="w-4 h-4" /></Link></div>
        </div>
      </div>
    );
  }

  const isLast = step === total - 1;

  return (
    <div>
      <SEO
        title="Free Roofing Estimate | Goral Construction"
        description="Answer 6 quick questions for a free roofing estimate in Oakland County, MI. We'll confirm your free on-site inspection within 24 hours."
        path="/get-estimate"
      />
      <section className="bg-bg-dark text-white py-14 relative overflow-hidden group">
        <DarkHeroAccent tone="shingles" />
        <div className="container-x relative">
          <div className="overline text-brand">Free estimate</div>
          <h1 className="font-heading font-extrabold text-3xl md:text-5xl tracking-tight mt-2">Tell us about your project.</h1>
          <div className="mt-8">
            <div className="flex justify-between text-xs uppercase tracking-widest text-white/60 mb-2"><span>Step {step + 1} of {total}</span><span>{Math.round(progress)}%</span></div>
            <div className="h-1 bg-white/20 rounded-full"><div className="h-1 bg-brand rounded-full transition-all duration-300" style={{ width: `${progress}%` }} /></div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container-x max-w-3xl">
          {step < STEPS.length ? (
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight">{STEPS[step].label}</h2>
              <div className="mt-8 grid sm:grid-cols-2 gap-3">
                {STEPS[step].options.map(opt => (
                  <button key={opt} onClick={() => choose(STEPS[step].key, opt)} className={`p-5 text-left border-2 rounded-sm font-semibold transition-all ${data[STEPS[step].key] === opt ? "border-brand bg-brand/5" : "border-ink-line bg-white hover:border-ink"}`} data-testid={`wizard-option-${opt.replace(/\W/g,'-')}`}>{opt}</button>
                ))}
              </div>
            </div>
          ) : (
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight">Where do we send the quote?</h2>
              <p className="text-ink-soft mt-3">We'll confirm your free inspection within 24 hours.</p>
              <div className="mt-8 space-y-4">
                <input required placeholder="Full name" value={data.name} onChange={e => setData(p => ({ ...p, name: e.target.value }))} className="w-full px-4 py-3 border border-ink-line focus:border-brand outline-none rounded-sm" data-testid="wizard-name" />
                <div className="grid md:grid-cols-2 gap-4">
                  <input required type="email" placeholder="Email" value={data.email} onChange={e => setData(p => ({ ...p, email: e.target.value }))} className="px-4 py-3 border border-ink-line focus:border-brand outline-none rounded-sm" data-testid="wizard-email" />
                  <input required placeholder="Phone" value={data.phone} onChange={e => setData(p => ({ ...p, phone: e.target.value }))} className="px-4 py-3 border border-ink-line focus:border-brand outline-none rounded-sm" data-testid="wizard-phone" />
                </div>
                <input placeholder="Property address (optional)" value={data.address} onChange={e => setData(p => ({ ...p, address: e.target.value }))} className="w-full px-4 py-3 border border-ink-line focus:border-brand outline-none rounded-sm" />
                <textarea rows={3} placeholder="Anything else we should know? (optional)" value={data.notes} onChange={e => setData(p => ({ ...p, notes: e.target.value }))} className="w-full px-4 py-3 border border-ink-line focus:border-brand outline-none rounded-sm resize-none" />
              </div>
            </div>
          )}

          <div className="mt-10 flex justify-between">
            <button onClick={() => setStep(s => Math.max(s - 1, 0))} disabled={step === 0} className="btn-secondary disabled:opacity-40" data-testid="wizard-back"><ArrowLeft className="w-4 h-4" /> Back</button>
            {isLast && (
              <button onClick={submit} disabled={!data.name || !data.email || !data.phone || status === "loading"} className="btn-primary disabled:opacity-50" data-testid="wizard-submit">
                {status === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {status === "loading" ? "Sending…" : "Submit estimate request"}
              </button>
            )}
          </div>
          {status === "error" && (
            <div className="mt-6 border-l-4 border-destructive bg-destructive/5 p-4" data-testid="wizard-error">
              <p className="font-heading font-bold text-destructive">Couldn't reach our server.</p>
              <p className="text-sm text-ink-soft mt-2 leading-relaxed">
                Please try again in a moment, or call us directly at{" "}
                <a href={`tel:${BUSINESS.phoneTel}`} className="font-bold text-ink underline">{BUSINESS.phone}</a>{" "}
                and we'll get your estimate started right away.
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
